/**
 * Smart Insights orchestrator streaming hook.
 *
 * Targets the Smart Insights orchestrator SSE endpoint directly.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { DashboardResponse } from '../../../services/api';
import {
    applyFrame,
    buildSyntheticEnvelope,
    createEmptyStore,
    type StreamingStore,
} from './streamingStore';
import { API_CONFIG } from '../../../config/api';
import {
    AUTH_REFRESH_FAILED_MESSAGE,
    AUTH_REFRESH_RETRY_LIMIT,
    UnauthorizedRetryError,
    getAccessTokenForAuthenticatedRequest,
    getStoredAccessToken,
    isUnauthorizedRetryError,
    refreshAccessTokenForRetry,
} from '../../../services/authRefresh';
export type PipelineType = 'main' | 'analysis' | 'visualization' | 'finalization';

export interface NodeProgress {
    name: string;
    displayName: string;
    details?: string;
    pipeline: PipelineType;
    status: 'pending' | 'running' | 'completed' | 'failed';
    duration?: number;
    timestamp?: string;
    summaryData?: Record<string, any>;
}

export interface TraceStep {
    id: string;
    parentId: string | null;
    name: string;
    displayName: string;
    kind: string;
    status: 'running' | 'ok' | 'error';
    startedAt?: number;
    durationMs?: number;
    summary?: string;
    milestone?: string;
}

export interface QueryProgress {
    percent: number;
    currentNode?: string | null;
    currentKind?: string | null;
    currentMessage?: string | null;
    currentDetails?: string | null;
    currentPipeline?: PipelineType | null;
    nodes: NodeProgress[];
    completedSteps: Array<{
        node: string;
        message: string;
        details?: string;
        pipeline?: PipelineType;
        duration?: number;
        timestamp?: string;
    }>;
    traceSteps: TraceStep[];
    isComplete: boolean;
    hasError: boolean;
    errorMessage?: string;
    totalDuration?: number;
    nodeTiming?: Record<string, number>;
    emptyDataShortCircuit?: boolean;
}

export interface UseQueryStreamReturn {
    sendQuery: (
        query: string,
        userId?: string,
        analysisMode?: 'normal' | 'deep' | 'simple',
        threadId?: string | null,
        resumeOverride?: Record<string, any>,
        simpleParameters?: Record<string, unknown>,
    ) => void;
    progress: QueryProgress;
    response: DashboardResponse | null;
    isConnected: boolean;
    isLoading: boolean;
    error: string | null;
    reset: () => void;
    botMessageId: string | null;
    humanMessageId: string | null;
    currentQuery: string | null;
    conversationId: string | null;
    canResumeInterrupt: () => boolean;
    setThreadId: (
        newId: string | null,
        pendingInterrupt?: { runId: string } | null,
    ) => void;
    setOnThreadCreated: (cb: ((threadId: string) => void) | null) => void;
}

type ChatV2Payload = {
    type?: string;
    text?: string;
    message?: string;
    summary?: string;
    name?: string;
    message_id?: string;
    structured?: Record<string, any>;
    reply?: string;
    error_code?: string;
    [key: string]: any;
};

type PendingInterrupt = {
    threadId: string;
    runId: string;
};

function clarificationResumePayload(answer: string): Record<string, any> {
    return {
        action: 'clarify',
        question: answer,
    };
}

type StreamDebugCategory =
    | 'lifecycle'
    | 'progress'
    | 'dashboard'
    | 'narrative'
    | 'error'
    | 'unknown';

type StreamDebugFrame = {
    at: string;
    type: string;
    category: StreamDebugCategory;
    stats: {
        charts: number;
        metrics: number;
        groups: number;
        hasSummary: boolean;
        hasNormalText: boolean;
        hasIntelligence: boolean;
    };
    payload: ChatV2Payload;
};

type StreamRequestDebugFrame = {
    at: string;
    phase: 'send' | 'open' | 'close' | 'error';
    requestId: string;
    endpoint: string;
    threadId: string;
    reusedThread: boolean;
    payload: Record<string, any>;
    status?: number;
    error?: string;
};

declare global {
    interface Window {
        __financeBotV2StreamFrames?: StreamDebugFrame[];
        __financeBotV2StreamSummary?: () => Record<string, number>;
        __financeBotV2DashboardPayloads?: () => Array<Record<string, any>>;
        __financeBotV2StreamRequests?: StreamRequestDebugFrame[];
    }
}

const initialProgress: QueryProgress = {
    percent: 0,
    nodes: [],
    completedSteps: [],
    traceSteps: [],
    isComplete: false,
    hasError: false,
};

const FRIENDLY_NODE_NAMES: Record<string, string> = {
    revenue_margin_actuals: 'Pulling Revenue And Margin Actuals',
    plan_vs_actual: 'Comparing Actuals Against Plan',
    project_period_revenue_margin: 'Projecting Period Revenue And Margin',
    top_drivers: 'Identifying The Top Drivers',
    lookup_data_dictionary: 'Looking Up The Data Dictionary',
};

function backendBase(): string {
    return (API_CONFIG.BACKEND_BASE_URL || '').replace(/\/$/, '');
}

function readAccessToken(): string | null {
    return getStoredAccessToken();
}

function getErrorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
}

function humanizeNode(nodeName: string): string {
    return FRIENDLY_NODE_NAMES[nodeName]
        || nodeName.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isRecord(value: unknown): value is Record<string, any> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function looksLikeSerializedObject(value: string): boolean {
    const text = value.trim();
    if (text === '[object Object]') return true;
    if (!/^[{[]/.test(text)) return false;
    try {
        const parsed = JSON.parse(text);
        return Boolean(parsed && typeof parsed === 'object');
    } catch {
        return false;
    }
}

function cleanDisplayText(value: string): string | undefined {
    const text = value.trim();
    if (!text || looksLikeSerializedObject(text)) return undefined;
    return text;
}

function streamDisplayText(value: unknown): string | undefined {
    if (typeof value === 'string') return cleanDisplayText(value);
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);

    if (Array.isArray(value)) {
        const scalarParts = value
            .filter((item) => item === null || typeof item !== 'object')
            .map(streamDisplayText)
            .filter(Boolean) as string[];
        return scalarParts.length === value.length ? cleanDisplayText(scalarParts.join(' · ')) : undefined;
    }

    if (isRecord(value)) {
        return streamDisplayText(value.summary)
            || streamDisplayText(value.text)
            || streamDisplayText(value.message)
            || streamDisplayText(value.detail)
            || streamDisplayText(value.details)
            || streamDisplayText(value.description);
    }

    return undefined;
}

function streamStepName(payload: ChatV2Payload): string {
    return streamDisplayText(payload.name)
        || streamDisplayText(payload.tool_name)
        || streamDisplayText(payload.node)
        || 'working';
}

function streamStepDetail(payload: ChatV2Payload): string | undefined {
    return streamDisplayText(payload.summary)
        || streamDisplayText(payload.text)
        || streamDisplayText(payload.message);
}

// Repo-2 parity: visible live-trace tool rows are derived from the tool
// `name` plus user-facing context. Backend argument dumps such as
// `measures=[...]` stay available in stream debug frames, but are not shown
// in the chat trace.
const FRIENDLY_TOOL_PHRASES: Record<string, string> = {
    revenue_margin_actuals: 'Pulling revenue and margin actuals',
    plan_vs_actual: 'Comparing actuals against plan',
    project_period_revenue_margin: 'Projecting period revenue and margin',
    top_drivers: 'Identifying the top drivers',
    lookup_data_dictionary: 'Looking up the data dictionary',
};

function humanisePeriod(period: unknown): string | null {
    if (typeof period !== 'string' || !period.trim()) return null;
    const map: Record<string, string> = {
        last_day: 'for yesterday',
        last_week: 'for last week',
        current_week: 'for this week',
        current_month: 'for this month',
        next_month: 'for next month',
        upcoming_month: 'for next month',
        last_month: 'for last month',
        current_quarter: 'for this quarter',
        next_quarter: 'for next quarter',
        upcoming_quarter: 'for next quarter',
        last_quarter: 'for last quarter',
        ytd: 'year-to-date',
        qtd: 'quarter-to-date',
        mtd: 'month-to-date',
        ttm: 'trailing 12 months',
        last_year: 'for last year',
        current_year: 'for this year',
        next_year: 'for next year',
        upcoming_year: 'for next year',
    };
    return map[period.toLowerCase()] || null;
}

function humaniseDimension(dimension: unknown): string | null {
    if (typeof dimension !== 'string' || !dimension.trim()) return null;
    const map: Record<string, string> = {
        channel: 'channel',
        subchannel: 'sub-channel',
        brand: 'brand',
        product_class: 'product class',
        product_subclass: 'product subclass',
        customer: 'customer',
        sold_to: 'customer',
        region: 'region',
        country: 'country',
    };
    return map[dimension.toLowerCase()] || dimension.toLowerCase().replace(/_/g, ' ');
}

function humaniseToolName(name: string): string {
    if (!name) return 'Working';
    const spaced = name.replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function parseLooseToolArguments(value: string | undefined): Record<string, unknown> | undefined {
    const text = value?.trim();
    if (!text) return undefined;

    try {
        const parsed = JSON.parse(text);
        return isRecord(parsed) ? parsed : undefined;
    } catch {
        // Continue with the loose `key=value` format emitted by some SSE tool summaries.
    }

    const args: Record<string, unknown> = {};
    const pattern = /([A-Za-z_][A-Za-z0-9_]*)=([^=]+?)(?=,\s*[A-Za-z_][A-Za-z0-9_]*=|$)/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text))) {
        const key = match[1];
        const rawValue = match[2].trim();
        if (rawValue.startsWith('[') && rawValue.endsWith(']')) {
            args[key] = rawValue
                .slice(1, -1)
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean);
        } else {
            args[key] = rawValue.replace(/^['"]|['"]$/g, '');
        }
    }

    return Object.keys(args).length > 0 ? args : undefined;
}

function streamToolArguments(payload: ChatV2Payload): Record<string, unknown> | undefined {
    const candidates = [
        payload.arguments,
        payload.args,
        payload.parameters,
        payload.input,
        payload.tool_args,
    ];

    for (const candidate of candidates) {
        if (isRecord(candidate)) return candidate;
        if (typeof candidate === 'string') {
            const parsed = parseLooseToolArguments(candidate);
            if (parsed) return parsed;
        }
    }

    return parseLooseToolArguments(streamStepDetail(payload));
}

function friendlyToolCallText(name: string, args: Record<string, unknown> | undefined): string {
    const base = FRIENDLY_TOOL_PHRASES[name] || humaniseToolName(name);
    const context: string[] = [];
    const period = humanisePeriod(args?.period);
    if (period) context.push(period);

    const groupBy = args?.group_by;
    const dimension = args?.dimension;
    const dimensions: string[] = [];
    if (Array.isArray(groupBy)) {
        for (const item of groupBy) {
            const label = humaniseDimension(item);
            if (label) dimensions.push(label);
        }
    } else {
        const label = humaniseDimension(dimension);
        if (label) dimensions.push(label);
    }
    if (dimensions.length > 0) context.push(`by ${dimensions.join(', ')}`);

    return context.length > 0 ? `${base} ${context.join(', ')}...` : `${base}...`;
}

function estimatePercent(nodes: NodeProgress[], isComplete = false): number {
    if (isComplete) return 100;
    const completed = nodes.filter((node) => node.status === 'completed').length;
    const running = nodes.some((node) => node.status === 'running') ? 1 : 0;
    return Math.min(95, Math.max(5, completed * 8 + running * 4));
}

function getStructuredPayloadStats(payload: ChatV2Payload): StreamDebugFrame['stats'] {
    const structured = payload.structured && typeof payload.structured === 'object'
        ? payload.structured
        : {};
    const blockMetrics = Array.isArray(structured.blocks)
        ? structured.blocks.reduce((count: number, block: any) => (
            block?.type === 'kpi_strip' && Array.isArray(block?.data?.items)
                ? count + block.data.items.length
                : count
        ), 0)
        : 0;
    const blockCharts = Array.isArray(structured.blocks)
        ? structured.blocks.filter((block: any) => block?.type === 'chart').length
        : 0;
    return {
        charts: Array.isArray(structured.charts) ? structured.charts.length : blockCharts,
        metrics: Array.isArray(structured.computation_results)
            ? structured.computation_results.length
            : blockMetrics,
        groups: Array.isArray(structured.groups) ? structured.groups.length : 0,
        hasSummary: Boolean(structured.summary || structured.analysis_summary || payload.reply),
        hasNormalText: Boolean(structured.normal_text),
        hasIntelligence: Boolean(structured.intelligence_analysis),
    };
}

function categorizeStreamPayload(payload: ChatV2Payload): StreamDebugCategory {
    const type = payload.type || 'message';
    const stats = getStructuredPayloadStats(payload);

    if (type === 'error') return 'error';
    if (type === 'user_saved') return 'lifecycle';
    if (stats.charts > 0 || stats.metrics > 0 || stats.groups > 0) {
        return 'dashboard';
    }
    if (type === 'thinking' || type === 'tool_call' || type === 'tool_result' || type === 'ping') {
        return 'progress';
    }
    if (type === 'content_delta' || type === 'final' || type === 'saved') {
        return 'narrative';
    }
    return 'unknown';
}

function recordStreamDebugFrame(payload: ChatV2Payload): void {
    if (typeof window === 'undefined') return;

    const frame: StreamDebugFrame = {
        at: new Date().toISOString(),
        type: payload.type || 'message',
        category: categorizeStreamPayload(payload),
        stats: getStructuredPayloadStats(payload),
        payload,
    };

    const frames = window.__financeBotV2StreamFrames || [];
    window.__financeBotV2StreamFrames = [...frames, frame].slice(-200);
    window.__financeBotV2StreamSummary = () => (
        (window.__financeBotV2StreamFrames || []).reduce<Record<string, number>>((acc, item) => {
            acc[item.category] = (acc[item.category] || 0) + 1;
            return acc;
        }, {})
    );
    window.__financeBotV2DashboardPayloads = () => (
        (window.__financeBotV2StreamFrames || [])
            .filter((item) => item.category === 'dashboard')
            .map((item) => item.payload.structured && typeof item.payload.structured === 'object'
                ? item.payload.structured
                : item.payload)
    );

    try {
        if (localStorage.getItem('financeBotV2StreamDebug') === '1') {
            console.debug('[FinanceBotV2 stream]', frame.category, frame.type, frame.stats, payload);
        }
    } catch {
        // Ignore storage access restrictions in private browsing contexts.
    }
}

function recordStreamRequestDebugFrame(frame: StreamRequestDebugFrame): void {
    if (typeof window === 'undefined') return;

    const frames = window.__financeBotV2StreamRequests || [];
    window.__financeBotV2StreamRequests = [...frames, frame].slice(-100);

    try {
        if (import.meta.env.DEV || localStorage.getItem('financeBotV2StreamDebug') === '1') {
            console.info('[FinanceBotV2 stream request]', frame);
        }
    } catch {
        if (import.meta.env.DEV) {
            console.info('[FinanceBotV2 stream request]', frame);
        }
    }
}

function adaptStructuredPayload(
    structured: Record<string, any> | undefined,
    query: string,
    reply?: string,
): DashboardResponse {
    const payload = structured && typeof structured === 'object' ? structured : {};
    const normalText = payload.normal_text || (!payload.charts?.length ? reply : undefined);
    const summary = payload.summary || reply || payload.analysis_summary?.summary_text;
    const nextActions = Array.isArray(payload.blocks)
        ? payload.blocks
            .filter((block: any) => block?.type === 'next_actions' && Array.isArray(block?.data?.actions))
            .flatMap((block: any) => block.data.actions)
            .map((action: any) => action?.query || action?.label)
            .filter((action: any) => typeof action === 'string' && action.trim())
        : [];

    return {
        query: String(payload.query || query),
        task: typeof payload.task === 'string' ? payload.task : undefined,
        charts: Array.isArray(payload.charts) ? payload.charts : [],
        insights: Array.isArray(payload.insights) ? payload.insights : [],
        analysis_summary: payload.analysis_summary,
        statistics: payload.statistics,
        metadata: {
            ...(payload.metadata || {}),
            schema: payload.$schema,
            run_id: payload.run_id,
            mode: payload.mode,
        },
        status: 'completed',
        errors: [],
        date_grouping: payload.date_grouping,
        computation_results: Array.isArray(payload.computation_results)
            ? payload.computation_results
            : [],
        sql_plan: payload.sql_plan,
        generated_sql: payload.generated_sql,
        selected_tables: payload.selected_tables,
        chart_plan: payload.chart_plan || payload.chart_preplan,
        operation_plan: payload.operation_plan,
        source_data: payload.source_data,
        source_data_metadata: payload.source_data_metadata,
        intelligence_analysis: payload.intelligence_analysis,
        normal_text: normalText,
        summary,
        $schema: payload.$schema,
        meta: payload.meta,
        query_context: payload.query_context,
        blocks: Array.isArray(payload.blocks) ? payload.blocks : undefined,
        tool_calls: payload.tool_calls,
        suggested_queries: Array.isArray(payload.suggested_queries)
            ? payload.suggested_queries
            : nextActions,
    } as DashboardResponse & { normal_text?: string; summary?: string };
}

async function createThread(query: string): Promise<string> {
    const title = query.trim().slice(0, 120) || 'Finance analysis';
    let token = await getAccessTokenForAuthenticatedRequest();
    if (!token) {
        throw new UnauthorizedRetryError(AUTH_REFRESH_FAILED_MESSAGE);
    }

    for (let attempt = 0; attempt <= AUTH_REFRESH_RETRY_LIMIT; attempt += 1) {
        const response = await fetch(`${backendBase()}/api/threads`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token || ''}`,
            },
            body: JSON.stringify({ title }),
        });

        if (response.status === 401) {
            const errorText = await response.text();
            if (attempt >= AUTH_REFRESH_RETRY_LIMIT) {
                throw new UnauthorizedRetryError(`thread create failed after token refresh: 401 ${errorText}`);
            }
            const refreshedToken = await refreshAccessTokenForRetry();
            if (!refreshedToken) {
                throw new Error(AUTH_REFRESH_FAILED_MESSAGE);
            }
            token = refreshedToken;
            continue;
        }

        if (!response.ok) {
            throw new Error(`thread create failed: ${response.status} ${await response.text()}`);
        }

        const body = await response.json();
        const id = body?.id || body?.thread_id;
        if (!id) {
            throw new Error('thread create failed: response did not include an id');
        }
        return String(id);
    }

    throw new Error(AUTH_REFRESH_FAILED_MESSAGE);
}

export function useQueryStream(): UseQueryStreamReturn {
    const [progress, setProgress] = useState<QueryProgress>(initialProgress);
    const [response, setResponse] = useState<DashboardResponse | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [threadId, setThreadIdState] = useState<string | null>(null);
    const [botMessageId, setBotMessageId] = useState<string | null>(null);
    const [humanMessageId, setHumanMessageId] = useState<string | null>(null);
    const [currentQuery, setCurrentQuery] = useState<string | null>(null);

    const ctrlRef = useRef<AbortController | null>(null);
    const threadIdRef = useRef<string | null>(null);
    const onThreadCreatedRef = useRef<((threadId: string) => void) | null>(null);
    const pendingFinalResponseRef = useRef<DashboardResponse | null>(null);
    const pendingInterruptRef = useRef<PendingInterrupt | null>(null);
    const finalFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Phase 2 — per-item streaming store. Mutates in place as the
    // backend's per-item frames (kpi_card_ready / chart_ready /
    // lens_item_ready / insight_finding / narrative_ready) arrive,
    // then we serialise it to a synthetic v2 envelope and push it
    // through ``adaptStructuredPayload`` so the UI re-renders
    // progressively. Reset on every new query in ``clearStreamState``.
    const streamingStoreRef = useRef<StreamingStore>(createEmptyStore());

    const stopActiveStream = useCallback(() => {
        if (ctrlRef.current) {
            ctrlRef.current.abort();
            ctrlRef.current = null;
        }
    }, []);

    const clearFinalFallbackTimer = useCallback(() => {
        if (finalFallbackTimerRef.current) {
            clearTimeout(finalFallbackTimerRef.current);
            finalFallbackTimerRef.current = null;
        }
    }, []);

    const clearStreamState = useCallback(() => {
        stopActiveStream();
        clearFinalFallbackTimer();
        pendingFinalResponseRef.current = null;
        streamingStoreRef.current = createEmptyStore();
        setProgress(initialProgress);
        setResponse(null);
        setError(null);
        setIsConnected(false);
        setIsLoading(false);
        setBotMessageId(null);
        setHumanMessageId(null);
    }, [clearFinalFallbackTimer, stopActiveStream]);

    const reset = useCallback(() => {
        clearStreamState();
        pendingInterruptRef.current = null;
        threadIdRef.current = null;
        setThreadIdState(null);
        setCurrentQuery(null);
    }, [clearStreamState]);

    useEffect(() => {
        return () => {
            stopActiveStream();
            clearFinalFallbackTimer();
        };
    }, [clearFinalFallbackTimer, stopActiveStream]);

    const setThreadId = useCallback((
        newId: string | null,
        pendingInterrupt?: { runId: string } | null,
    ) => {
        clearStreamState();
        pendingInterruptRef.current = newId && pendingInterrupt
            ? { threadId: newId, ...pendingInterrupt }
            : null;
        threadIdRef.current = newId;
        setThreadIdState(newId);
        setCurrentQuery(null);
    }, [clearStreamState]);

    const setOnThreadCreated = useCallback((cb: ((threadId: string) => void) | null) => {
        onThreadCreatedRef.current = cb;
    }, []);

    const canResumeInterrupt = useCallback(() => {
        const pendingInterrupt = pendingInterruptRef.current;
        return Boolean(
            pendingInterrupt
            && pendingInterrupt.threadId === threadIdRef.current
            && pendingInterrupt.runId
        );
    }, []);

    const handlePayload = useCallback((
        payload: ChatV2Payload,
        query: string,
        controller: AbortController,
    ) => {
        const type = payload.type || 'message';

        switch (type) {
            case 'user_saved':
                if (payload.message_id) {
                    setHumanMessageId(String(payload.message_id));
                }
                break;

            case 'thinking': {
                const text = streamDisplayText(payload.text)
                    || streamDisplayText(payload.message)
                    || 'Working on your question';
                setProgress((prev) => {
                    const lastStep = prev.completedSteps[prev.completedSteps.length - 1];
                    const lastStepText = lastStep?.details || lastStep?.message;
                    const shouldAppendThinkingStep = text && lastStepText !== text;

                    return {
                        ...prev,
                        percent: Math.max(prev.percent, 5),
                        completedSteps: shouldAppendThinkingStep
                            ? [
                                ...prev.completedSteps,
                                {
                                    node: `thinking:${Date.now()}`,
                                    message: text,
                                    details: text,
                                },
                            ]
                            : prev.completedSteps,
                        currentNode: undefined,
                        currentKind: undefined,
                        currentMessage: text,
                        currentDetails: text,
                    };
                });
                break;
            }

            case 'tool_call':
            case 'tool_result':
                // Legacy per-tool events. Trace tree now consumes
                // ``trace_step`` / ``trace_step_end`` instead, so these
                // are intentionally swallowed — keeping them as no-ops
                // avoids duplicate rows in the live trace.
                break;

            // Langfuse-style trace tree events. Each ``trace_step``
            // opens a new row in the live trace; ``trace_step_end``
            // closes it with a duration and a one-line summary
            // produced by the backend node itself (e.g.
            // ``"4 cards"`` / ``"9 findings"``). The FE links rows
            // into a tree via ``parent_id`` so the chat surface
            // mirrors the Langfuse hierarchy.
            case 'trace_step': {
                const id = String(payload.id || '');
                if (!id) break;
                const step: NonNullable<QueryProgress['traceSteps']>[number] = {
                    id,
                    parentId: payload.parent_id ? String(payload.parent_id) : null,
                    name: String(payload.name || ''),
                    displayName: String(payload.display_name || payload.name || ''),
                    kind: (String(payload.kind || 'node') as any),
                    status: 'running',
                    startedAt: Date.now(),
                };
                setProgress((prev) => {
                    const existing = prev.traceSteps || [];
                    if (existing.some((s) => s.id === id)) return prev;
                    return {
                        ...prev,
                        traceSteps: [...existing, step],
                        currentNode: step.name,
                        currentKind: step.kind,
                        currentMessage: step.displayName,
                    };
                });
                break;
            }

            case 'trace_step_end': {
                const id = String(payload.id || '');
                if (!id) break;
                const durationMs = typeof payload.duration_ms === 'number'
                    ? payload.duration_ms
                    : undefined;
                const status: 'ok' | 'error' = payload.status === 'error' ? 'error' : 'ok';
                const summary = payload.summary ? String(payload.summary) : undefined;
                const milestone = payload.milestone ? String(payload.milestone) : undefined;
                setProgress((prev) => {
                    const existing = prev.traceSteps || [];
                    const next = existing.map((s) =>
                        s.id === id
                            ? { ...s, status, durationMs, summary, milestone }
                            : s,
                    );
                    return {
                        ...prev,
                        traceSteps: next,
                        currentMessage: milestone || summary || prev.currentMessage,
                    };
                });
                break;
            }

            case 'content_delta': {
                const text = streamDisplayText(payload.text) || streamDisplayText(payload.message);
                if (text) {
                    setProgress((prev) => (
                        prev.currentNode
                            ? prev
                            : {
                                ...prev,
                                currentMessage: text,
                                currentDetails: text,
                            }
                    ));
                }
                break;
            }

            case 'interrupt': {
                const interrupts = Array.isArray(payload.interrupts) ? payload.interrupts : [];
                const first = interrupts[0]?.value && typeof interrupts[0].value === 'object'
                    ? interrupts[0].value
                    : {};
                const runId = streamDisplayText(first.run_id) || streamDisplayText(payload.trace_id);
                const interruptThreadId = streamDisplayText(first.thread_id) || threadIdRef.current || '';
                if (runId && interruptThreadId) {
                    pendingInterruptRef.current = {
                        threadId: interruptThreadId,
                        runId,
                    };
                }
                const message = streamDisplayText(first.message) || 'I need a bit more information before continuing.';
                const followUp = streamDisplayText(first.follow_up_question);
                const text = followUp ? `${message}\n\n${followUp}` : message;
                clearFinalFallbackTimer();
                pendingFinalResponseRef.current = null;
                setResponse(adaptStructuredPayload(
                    {
                        query,
                        normal_text: text,
                        summary: text,
                        meta: {
                            interrupt: true,
                            interrupt_options: first.options,
                        },
                    },
                    query,
                    text,
                ));
                setProgress((prev) => ({
                    ...prev,
                    percent: 100,
                    isComplete: true,
                    hasError: false,
                    errorMessage: undefined,
                    currentNode: undefined,
                    currentMessage: text,
                    currentDetails: text,
                }));
                setIsLoading(false);
                break;
            }

            case 'final': {
                const dashboardResponse = adaptStructuredPayload(payload.structured, query, streamDisplayText(payload.reply));
                pendingFinalResponseRef.current = dashboardResponse;
                clearFinalFallbackTimer();
                finalFallbackTimerRef.current = setTimeout(() => {
                    setResponse(dashboardResponse);
                    pendingFinalResponseRef.current = null;
                    finalFallbackTimerRef.current = null;
                }, 800);
                // BE empty-data short-circuit flag — flows from
                // ``empty_data_node.meta.empty_data_short_circuit`` through
                // ``structured.meta`` and lands here. The FE uses it to
                // hide the live-trace card and skip any DashboardReadyCard.
                const emptyDataShortCircuit = Boolean(
                    payload.structured?.meta?.empty_data_short_circuit
                );
                setProgress((prev) => ({
                    ...prev,
                    percent: 100,
                    isComplete: true,
                    hasError: false,
                    errorMessage: undefined,
                    emptyDataShortCircuit,
                }));
                setIsLoading(false);
                break;
            }

            case 'saved': {
                clearFinalFallbackTimer();
                const dashboardResponse = payload.structured
                    ? adaptStructuredPayload(payload.structured, query, streamDisplayText(payload.reply))
                    : pendingFinalResponseRef.current;
                if (payload.message_id) {
                    setBotMessageId(String(payload.message_id));
                }
                if (dashboardResponse) {
                    setResponse(dashboardResponse);
                }
                pendingFinalResponseRef.current = null;
                const emptyDataShortCircuit = Boolean(
                    payload.structured?.meta?.empty_data_short_circuit
                );
                setProgress((prev) => ({
                    ...prev,
                    percent: 100,
                    isComplete: true,
                    hasError: false,
                    errorMessage: undefined,
                    emptyDataShortCircuit: prev.emptyDataShortCircuit || emptyDataShortCircuit,
                }));
                setIsLoading(false);
                setTimeout(() => {
                    if (!controller.signal.aborted) {
                        controller.abort();
                    }
                }, 50);
                break;
            }

            case 'error': {
                if (payload.message_id) {
                    setBotMessageId(String(payload.message_id));
                }
                const message = streamDisplayText(payload.message) || streamDisplayText(payload.error) || 'Pipeline error';
                const errorCode = streamDisplayText(payload.error_code);
                const suffix = errorCode ? `\n\nError Code: ${errorCode}` : '';
                const fullMessage = `${message}${suffix}`;
                setError(String(message));
                setProgress((prev) => ({
                    ...prev,
                    hasError: true,
                    errorMessage: fullMessage,
                    isComplete: false,
                    currentNode: undefined,
                    currentMessage: undefined,
                }));
                setIsLoading(false);
                if (!controller.signal.aborted) {
                    controller.abort();
                }
                break;
            }

            case 'ping':
                break;

            case 'node_complete':
                // Trace tree is driven by ``trace_step`` / ``trace_step_end``.
                // ``node_complete`` is a legacy state envelope and would
                // duplicate or flatten the real trace rows if surfaced.
                break;

            case 'node_progress':
                // Forwarder noise — handled per-block by the partial
                // event branch above.
                break;

            // Phase 2 — per-item streaming frames. Each frame mutates
            // the streaming store; we then serialise the store into a
            // synthetic v2 envelope and push it through the same
            // ``adaptStructuredPayload`` chain the ``final`` frame
            // uses. The UI re-renders incrementally — KPI cards
            // appear one by one, chart tiles light up as they
            // materialise, lens buckets fill as the curator emits
            // items.
            //
            // The ``final`` / ``saved`` cases below still set the
            // authoritative response from the full envelope, which
            // wins over the streaming view (acts as a reconciler).
            // Node-level lifecycle frames produce trace rows.
            // ``block_started`` opens a "running" row for the inner
            // sub-node (kpi_generator / chart_builder /
            // insight_analyzer / insight_curator); ``block_complete``
            // resolves it to "completed" with the item count. Per-item
            // frames (kpi_card_ready / chart_ready / lens_item_ready /
            // insight_finding) only update the running node's
            // currentMessage so the live state shows what's being
            // processed — they DON'T accumulate as separate trace
            // rows. Result: the trace card shows node flows + tool
            // calls only; per-item churn flashes through the active
            // node's status line and disappears once the node closes.
            case 'block_started':
            case 'block_complete': {
                // Per-block frames drive the canvas streaming store
                // (KPI cards / charts / lens items appearing
                // progressively). Trace rows are now produced by
                // ``trace_step`` / ``trace_step_end`` from the backend
                // nodes themselves, so we only mutate the store here.
                applyFrame(streamingStoreRef.current, type, payload);
                const envelope = buildSyntheticEnvelope(streamingStoreRef.current, query);
                setResponse(adaptStructuredPayload(envelope, query));
                break;
            }

            // Per-item frames — update the streaming store + the
            // running node's currentMessage. NO new trace rows: the
            // user explicitly asked that only node + tool-call rows
            // appear in the trace, with per-item activity visible
            // only as transient live state on the running node.
            case 'kpi_card_ready':
            case 'chart_ready':
            case 'chart_dropped':
            case 'lens_item_ready':
            case 'insight_finding':
            case 'insights_prose_ready':
            case 'executive_summary_ready':
            case 'follow_up_ready':
            case 'narrative_ready': {
                applyFrame(streamingStoreRef.current, type, payload);
                const envelope = buildSyntheticEnvelope(streamingStoreRef.current, query);
                setResponse(adaptStructuredPayload(envelope, query));

                let liveMsg = '';
                if (type === 'kpi_card_ready') {
                    const card = (payload.card && typeof payload.card === 'object')
                        ? payload.card as Record<string, any> : {};
                    liveMsg = `Building ${card.label || card.kpi_id || 'KPI'}…`;
                } else if (type === 'chart_ready') {
                    const spec = (payload.chart_spec && typeof payload.chart_spec === 'object')
                        ? payload.chart_spec as Record<string, any> : {};
                    liveMsg = `Building ${spec.title || spec.graph_id || 'chart'}…`;
                } else if (type === 'lens_item_ready') {
                    const bucket = String(payload.bucket || 'lens');
                    liveMsg = `Curating ${bucket}…`;
                } else if (type === 'insight_finding') {
                    const lane = payload.lane ? String(payload.lane) : 'finding';
                    liveMsg = `Detecting ${lane}…`;
                } else if (type === 'narrative_ready') {
                    liveMsg = 'Drafting narrative…';
                }
                if (liveMsg) {
                    setProgress((prev) => ({
                        ...prev,
                        currentMessage: liveMsg,
                        currentDetails: liveMsg,
                    }));
                }
                break;
            }

            default:
                {
                    const text = streamDisplayText(payload.message) || streamDisplayText(payload.text);
                    if (!text) break;
                    setProgress((prev) => ({
                        ...prev,
                        currentMessage: text || prev.currentMessage,
                    }));
                }
        }
    }, [clearFinalFallbackTimer]);

    const sendQuery = useCallback(
        async (
            query: string,
            _userId: string = 'default-user',
            analysisMode: 'normal' | 'deep' | 'simple' = 'normal',
            threadIdOverride?: string | null,
            resumeOverride?: Record<string, any>,
            simpleParameters?: Record<string, unknown>,
        ) => {
            const requestedThreadId =
                threadIdOverride !== undefined ? threadIdOverride : threadIdRef.current;
            const pendingInterrupt = pendingInterruptRef.current;
            const shouldResumeInterrupt = Boolean(
                pendingInterrupt
                && pendingInterrupt.threadId === requestedThreadId
                && pendingInterrupt.runId
            );
            if (resumeOverride && !shouldResumeInterrupt) return;

            clearStreamState();
            setIsLoading(true);
            setCurrentQuery(query);

            const reusedThread = Boolean(requestedThreadId);
            let effectiveThreadId = requestedThreadId;

            try {
                if (!effectiveThreadId) {
                    effectiveThreadId = await createThread(query);
                    threadIdRef.current = effectiveThreadId;
                    setThreadIdState(effectiveThreadId);
                    if (onThreadCreatedRef.current) {
                        onThreadCreatedRef.current(effectiveThreadId);
                    }
                } else {
                    threadIdRef.current = effectiveThreadId;
                    setThreadIdState(effectiveThreadId);
                }
            } catch (err: unknown) {
                const message = isUnauthorizedRetryError(err)
                    ? AUTH_REFRESH_FAILED_MESSAGE
                    : getErrorMessage(err, 'Failed to create chat thread');
                setError(message);
                setIsLoading(false);
                setProgress((prev) => ({
                    ...prev,
                    hasError: true,
                    errorMessage: message,
                }));
                return;
            }

            const controller = new AbortController();
            ctrlRef.current = controller;
            const requestId = `finance-v2-stream-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const endpoint = shouldResumeInterrupt
                ? `${backendBase()}/api/threads/${effectiveThreadId}/orchestrator/resume/stream`
                : `${backendBase()}/api/threads/${effectiveThreadId}/orchestrator/stream`;
            // Backend ThreadChatRequest expects ``mode: "normal" | "deep"``.
            // The Deep toggle in ChatInput is the only gate for the
            // actionable-insight reasoning layer — sending the user's
            // selected mode here is what activates that node downstream.
            const requestPayload = shouldResumeInterrupt
                ? {
                    run_id: pendingInterrupt?.runId,
                    resume: resumeOverride ?? clarificationResumePayload(query),
                }
                : {
                    message: query,
                    mode: analysisMode,
                    ...(analysisMode === 'simple' ? { parameters: simpleParameters || {} } : {}),
                };

            recordStreamRequestDebugFrame({
                at: new Date().toISOString(),
                phase: 'send',
                requestId,
                endpoint,
                threadId: effectiveThreadId,
                reusedThread,
                payload: requestPayload,
            });

            let streamToken = await getAccessTokenForAuthenticatedRequest();
            if (!streamToken) {
                const message = AUTH_REFRESH_FAILED_MESSAGE;
                recordStreamRequestDebugFrame({
                    at: new Date().toISOString(),
                    phase: 'error',
                    requestId,
                    endpoint,
                    threadId: effectiveThreadId,
                    reusedThread,
                    payload: requestPayload,
                    error: message,
                });
                setError(message);
                setProgress((prev) => ({
                    ...prev,
                    hasError: true,
                    errorMessage: message,
                    isComplete: false,
                }));
                setIsLoading(false);
                setIsConnected(false);
                return;
            }
            let streamAttempt = 0;

            while (streamAttempt <= AUTH_REFRESH_RETRY_LIMIT) {
                try {
                    await fetchEventSource(
                        endpoint,
                        {
                            method: 'POST',
                            signal: controller.signal,
                            openWhenHidden: true,
                            headers: {
                                'Content-Type': 'application/json',
                                Accept: 'text/event-stream',
                                Authorization: `Bearer ${streamToken || ''}`,
                            },
                            body: JSON.stringify(requestPayload),
                            onopen: async (res) => {
                                recordStreamRequestDebugFrame({
                                    at: new Date().toISOString(),
                                    phase: 'open',
                                    requestId,
                                    endpoint,
                                    threadId: effectiveThreadId,
                                    reusedThread,
                                    payload: requestPayload,
                                    status: res.status,
                                });
                                if (res.ok) {
                                    if (
                                        shouldResumeInterrupt
                                        && pendingInterruptRef.current === pendingInterrupt
                                    ) {
                                        pendingInterruptRef.current = null;
                                    }
                                    setIsConnected(true);
                                    return;
                                }
                                const errorText = await res.text();
                                if (res.status === 401) {
                                    throw new UnauthorizedRetryError(`stream open failed: 401 ${errorText}`);
                                }
                                throw new Error(`stream open failed: ${res.status} ${errorText}`);
                            },
                            onmessage: (event) => {
                                if (!event.data) return;
                                try {
                                    const parsedRaw = JSON.parse(event.data) as ChatV2Payload;
                                    const namedEventType = event.event && event.event !== 'message'
                                        ? event.event
                                        : undefined;
                                    const parsed = parsedRaw.type || !namedEventType
                                        ? parsedRaw
                                        : { ...parsedRaw, type: namedEventType };
                                    recordStreamDebugFrame(parsed);
                                    handlePayload(parsed, query, controller);
                                } catch (err) {
                                    console.error('[FinanceBotV2 SSE] Failed to parse event', err, event.data);
                                }
                            },
                            onerror: (err) => {
                                throw err;
                            },
                            onclose: () => {
                                recordStreamRequestDebugFrame({
                                    at: new Date().toISOString(),
                                    phase: 'close',
                                    requestId,
                                    endpoint,
                                    threadId: effectiveThreadId,
                                    reusedThread,
                                    payload: requestPayload,
                                });
                                setIsConnected(false);
                                setIsLoading(false);
                            },
                        },
                    );
                    return;
                } catch (err: unknown) {
                    if (err instanceof Error && err.name === 'AbortError') {
                        return;
                    }

                    if (isUnauthorizedRetryError(err) && streamAttempt < AUTH_REFRESH_RETRY_LIMIT) {
                        const refreshedToken = await refreshAccessTokenForRetry();
                        if (refreshedToken) {
                            streamAttempt += 1;
                            streamToken = refreshedToken;
                            setIsConnected(false);
                            setIsLoading(true);
                            continue;
                        }
                    }

                    const message = isUnauthorizedRetryError(err)
                        ? AUTH_REFRESH_FAILED_MESSAGE
                        : getErrorMessage(err, 'stream failed');
                    recordStreamRequestDebugFrame({
                        at: new Date().toISOString(),
                        phase: 'error',
                        requestId,
                        endpoint,
                        threadId: effectiveThreadId,
                        reusedThread,
                        payload: requestPayload,
                        error: message,
                    });
                    setError(message);
                    setProgress((prev) => ({
                        ...prev,
                        hasError: true,
                        errorMessage: message,
                        isComplete: false,
                    }));
                    setIsLoading(false);
                    setIsConnected(false);
                    return;
                }
            }
        },
        [clearStreamState, handlePayload],
    );

    return {
        sendQuery,
        progress,
        response,
        isConnected,
        isLoading,
        error,
        reset,
        botMessageId,
        humanMessageId,
        currentQuery,
        conversationId: threadId,
        canResumeInterrupt,
        setThreadId,
        setOnThreadCreated,
    };
}

export default useQueryStream;
